import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import {
  LayoutDashboard, Crown, MessageSquare, ListChecks, Megaphone,
  Play, CheckCircle2, XCircle, RefreshCw, ArrowLeft, LogOut,
  ChevronDown, Send, Sparkles, Target, PenTool, Search, Share2, Mail, Rocket,
  Linkedin, Link2, Unlink, TrendingUp, Eye, Heart, MessageCircle, ExternalLink,
  Filter, CheckSquare, Square, Award, Menu, X as CloseIcon, Edit2, Trash2, Save
} from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import axios from "axios";
import { C, AGENTS, STATUS, CHANNELS, TIMEFRAMES } from "./theme";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ─── Extracted animation variants ─── */
const fadeIn = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const fadeInUp = (delay = 0) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay } });
const expandCollapse = {
  initial: { height: 0, opacity: 0 },
  animate: { height: "auto", opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.2 }
};

const AGENT_ICONS = {
  director: Crown, content_writer: PenTool, seo_agent: Search,
  social_media: Share2, email_marketing: Mail, outreach: Rocket
};

const STATUS_LABELS = {
  all: "All", pending: "Pending", review: "Review", approved: "Approved", rejected: "Rejected", failed: "Failed"
};

/* ─── LinkedIn Post Preview — WYSIWYG exactly how it will look on LinkedIn ─── */
const LinkedInPreview = ({ text, authorName, authorTitle, authorPicture, targetLabel, isCompany }) => {
  // Tokenize text to highlight hashtags, mentions, URLs like LinkedIn does
  const renderLine = (line, lineIdx) => {
    const parts = line.split(/(\s+)/);
    return parts.map((p, i) => {
      if (/^#\w+/.test(p)) {
        return <span key={`${lineIdx}-${i}`} style={{ color: "#0A66C2", fontWeight: 500 }}>{p}</span>;
      }
      if (/^@\w+/.test(p)) {
        return <span key={`${lineIdx}-${i}`} style={{ color: "#0A66C2", fontWeight: 500 }}>{p}</span>;
      }
      if (/^https?:\/\//.test(p)) {
        return <span key={`${lineIdx}-${i}`} style={{ color: "#0A66C2" }}>{p}</span>;
      }
      return <span key={`${lineIdx}-${i}`}>{p}</span>;
    });
  };

  const lines = (text || "").split("\n");

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: "#FFFFFF",
        color: "#1D2226",
        border: "1px solid #E0E0E0",
        boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      {/* Header */}
      <div className="p-3 flex items-start gap-2">
        {authorPicture ? (
          <img src={authorPicture} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div
            className={`w-12 h-12 ${isCompany ? 'rounded-md' : 'rounded-full'} flex items-center justify-center flex-shrink-0`}
            style={{ background: "#EDEDED", color: "#666", fontSize: 18, fontWeight: 600 }}
          >
            {(authorName || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0 leading-tight">
          <p className="text-sm font-bold truncate" style={{ color: "#1D2226" }}>{authorName || "Your name"}</p>
          {authorTitle && <p className="text-xs truncate" style={{ color: "#666" }}>{authorTitle}</p>}
          <p className="text-[11px] mt-0.5" style={{ color: "#666" }}>
            Just now · <span style={{ fontSize: 10 }}>🌐</span>
          </p>
        </div>
        <button className="text-gray-400 text-xl leading-none pt-1">⋯</button>
      </div>

      {/* Body */}
      <div className="px-3 pb-3">
        <div className="text-[14px] leading-[1.45]" style={{ color: "#1D2226", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {lines.length > 0 ? lines.map((line, idx) => (
            <div key={idx}>{renderLine(line, idx)}{idx < lines.length - 1 ? "" : ""}</div>
          )) : <span style={{ color: "#999" }}>Your post will appear here…</span>}
        </div>
      </div>

      {/* Engagement bar */}
      <div className="px-3 py-1.5 text-xs flex items-center justify-between" style={{ borderTop: "1px solid #F2F2F2", color: "#666" }}>
        <span className="flex items-center gap-1">
          <span className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: "#0A66C2", color: "#fff", fontSize: 10 }}>👍</span>
          <span className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: "#DF704D", color: "#fff", fontSize: 10 }}>❤</span>
          <span className="text-[11px]">0</span>
        </span>
        <span className="text-[11px]">0 comments · 0 reposts</span>
      </div>
      <div className="grid grid-cols-4 text-xs" style={{ borderTop: "1px solid #F2F2F2" }}>
        {["👍 Like", "💬 Comment", "🔁 Repost", "➤ Send"].map(label => (
          <button key={label} className="py-2 text-center font-semibold" style={{ color: "#666" }}>{label}</button>
        ))}
      </div>
    </div>
  );
};

/* ─── Extract just the post body from Social Media agent markdown output ─── */
const extractPostText = (output) => {
  if (!output) return "";
  const src = output.toString();

  // 1. Look for ``` code block(s) after "### Post:" or "## Post:"
  const postHeaderMatch = src.match(/#{2,3}\s*Post\s*:?/i);
  if (postHeaderMatch) {
    const after = src.slice(postHeaderMatch.index + postHeaderMatch[0].length);
    // Prefer code block
    const fenceMatch = after.match(/```[a-z]*\s*\n([\s\S]*?)\n?```/);
    if (fenceMatch) return fenceMatch[1].trim();
    // Otherwise take text up to next ### or end
    const upTo = after.search(/\n#{2,3}\s/);
    const body = upTo > 0 ? after.slice(0, upTo) : after;
    return body.replace(/^\s*\n+/, "").trim();
  }

  // 2. Fallback: first fenced code block
  const firstFence = src.match(/```[a-z]*\s*\n([\s\S]*?)\n?```/);
  if (firstFence) return firstFence[1].trim();

  // 3. Fallback: strip scaffolding (lines starting with # or bold labels)
  return src
    .split("\n")
    .filter(line => {
      const l = line.trim();
      if (/^#{1,6}\s/.test(l)) return false;
      if (/^(Platform|Framework used|Why this works|Optimal posting time|First comment to add|Hashtags|Visual concept)\s*:/i.test(l)) return false;
      return true;
    })
    .join("\n")
    .trim();
};

/* ─── Markdown renderer for AI outputs — compact styling ─── */
const MDContent = ({ children, color }) => (
  <div
    className="markdown-content text-sm leading-relaxed"
    style={{ color: color || `${C.text}cc` }}
  >
    <ReactMarkdown
      components={{
        h1: ({ node, ...p }) => <h1 className="text-lg font-bold mt-4 mb-2" style={{ color: C.text }} {...p} />,
        h2: ({ node, ...p }) => <h2 className="text-base font-bold mt-3 mb-1.5" style={{ color: C.text }} {...p} />,
        h3: ({ node, ...p }) => <h3 className="text-sm font-semibold mt-3 mb-1" style={{ color: C.text }} {...p} />,
        p:  ({ node, ...p }) => <p className="mb-2.5" {...p} />,
        ul: ({ node, ...p }) => <ul className="list-disc pl-5 space-y-1 mb-3" {...p} />,
        ol: ({ node, ...p }) => <ol className="list-decimal pl-5 space-y-1 mb-3" {...p} />,
        li: ({ node, ...p }) => <li className="text-sm" {...p} />,
        strong: ({ node, ...p }) => <strong style={{ color: C.text }} {...p} />,
        em: ({ node, ...p }) => <em {...p} />,
        code: ({ node, inline, ...p }) => inline
          ? <code className="px-1 py-0.5 rounded text-xs" style={{ background: `${C.primary}12`, color: C.primary }} {...p} />
          : <code className="block p-3 rounded-lg overflow-x-auto text-xs my-2" style={{ background: C.bg, color: C.text }} {...p} />,
        a: ({ node, ...p }) => <a className="underline" style={{ color: C.primary }} target="_blank" rel="noopener noreferrer" {...p} />,
        blockquote: ({ node, ...p }) => <blockquote className="border-l-2 pl-3 italic my-2" style={{ borderColor: `${C.primary}40`, color: `${C.text}99` }} {...p} />,
      }}
    >
      {children || ""}
    </ReactMarkdown>
  </div>
);

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "director", label: "Director", icon: Crown },
  { id: "chat", label: "Agent Chat", icon: MessageSquare },
  { id: "tasks", label: "All Tasks", icon: ListChecks },
  { id: "campaigns", label: "Campaigns", icon: Megaphone },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
];

/* ─── Reusable pill for status ─── */
const StatusPill = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span
      className="px-2.5 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wider whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}
      data-testid={`status-${status}`}
    >
      {status}
    </span>
  );
};

/* ─── Card wrapper with depth ─── */
const Card = ({ children, className = "", glow, style = {}, ...props }) => (
  <div
    className={`rounded-2xl ${className}`}
    style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      boxShadow: glow ? `0 0 30px ${glow}15, inset 0 1px 0 ${glow}08` : `0 2px 8px rgba(0,0,0,0.3)`,
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
);

/* ─── Main Dashboard Component ─── */
const AgentDashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [perAgent, setPerAgent] = useState([]);
  const [readyToPublish, setReadyToPublish] = useState([]);
  const [recentlyPublished, setRecentlyPublished] = useState([]);
  const [engagementTimeline, setEngagementTimeline] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);

  // Director
  const [planGoal, setPlanGoal] = useState("");
  const [planChannels, setPlanChannels] = useState(["twitter", "linkedin", "email"]);
  const [planTimeframe, setPlanTimeframe] = useState("1 week");
  const [planLoading, setPlanLoading] = useState(false);
  const [planProgress, setPlanProgress] = useState("");
  const [planResult, setPlanResult] = useState(null);

  // Chat
  const [chatAgent, setChatAgent] = useState("director");
  const [chatPrompt, setChatPrompt] = useState("");
  const [chatResponse, setChatResponse] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);

  // Actions
  const [executingAll, setExecutingAll] = useState(false);
  const [reviewFeedback, setReviewFeedback] = useState("");
  const [loading, setLoading] = useState(false);

  // Task filters + bulk selection
  const [taskFilterStatus, setTaskFilterStatus] = useState("all");
  const [taskFilterAgent, setTaskFilterAgent] = useState("all");
  const [taskSearch, setTaskSearch] = useState("");
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Mobile navigation
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Campaign edit
  const [editingCampaign, setEditingCampaign] = useState(null); // {id, name, goal}

  // Publish preview modal
  const [publishModal, setPublishModal] = useState(null); // { task_id, text, target, phase: 'preview'|'publishing'|'success'|'failed', resultUrl, error }

  // LinkedIn
  const [linkedinStatus, setLinkedinStatus] = useState({ connected: false });
  const [linkedinPosts, setLinkedinPosts] = useState([]);
  const [linkedinSummary, setLinkedinSummary] = useState(null);
  const [linkedinOrgs, setLinkedinOrgs] = useState({ organizations: [], api_available: false, note: "" });
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const [newPostText, setNewPostText] = useState("");
  const [postTarget, setPostTarget] = useState("personal");
  const [publishing, setPublishing] = useState(false);
  const [refreshingPostId, setRefreshingPostId] = useState(null);
  const [linkedinBanner, setLinkedinBanner] = useState(null);

  // Memoized filtered tasks
  const reviewTasks = useMemo(() => tasks.filter(t => t.status === "review").slice(0, 5), [tasks]);

  const filteredTasks = useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    return tasks.filter(t => {
      if (taskFilterStatus !== "all" && t.status !== taskFilterStatus) return false;
      if (taskFilterAgent !== "all" && t.agent_type !== taskFilterAgent) return false;
      if (q) {
        const hay = `${t.title || ""} ${t.output || ""} ${t.prompt || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, taskFilterStatus, taskFilterAgent, taskSearch]);

  const taskStatusCounts = useMemo(() => {
    const c = { all: tasks.length, pending: 0, review: 0, approved: 0, rejected: 0, failed: 0 };
    tasks.forEach(t => { if (c[t.status] !== undefined) c[t.status] += 1; });
    return c;
  }, [tasks]);

  const toggleTaskSelection = (id) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    const ids = filteredTasks.map(t => t.id);
    setSelectedTaskIds(prev => {
      const allSelected = ids.every(id => prev.has(id));
      return allSelected ? new Set() : new Set(ids);
    });
  };

  const bulkReviewAction = async (action) => {
    if (selectedTaskIds.size === 0) return;
    if (!window.confirm(`${action === "approve" ? "Approve" : "Reject"} ${selectedTaskIds.size} task(s)?`)) return;
    setBulkActionLoading(true);
    try {
      const res = await axios.post(`${API}/agents/tasks/bulk-review`, {
        task_ids: Array.from(selectedTaskIds),
        action,
      });
      setSelectedTaskIds(new Set());
      loadDashboard();
      alert(`${res.data.updated} task(s) ${action}d.`);
    } catch (err) {
      alert(err.response?.data?.detail || "Bulk action failed");
    }
    setBulkActionLoading(false);
  };

  const loadDashboard = useCallback(async () => {
    try {
      const [dashRes, campRes, taskRes] = await Promise.all([
        axios.get(`${API}/agents/dashboard`),
        axios.get(`${API}/agents/campaigns`),
        axios.get(`${API}/agents/tasks`)
      ]);
      setStats(dashRes.data.stats);
      setPerAgent(dashRes.data.per_agent || []);
      setReadyToPublish(dashRes.data.ready_to_publish || []);
      setRecentlyPublished(dashRes.data.recently_published || []);
      setEngagementTimeline(dashRes.data.engagement_timeline || []);
      setCampaigns(campRes.data.campaigns);
      setTasks(taskRes.data.tasks);
    } catch { /* dashboard load error */ }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const loadCampaignDetail = async (campaignId) => {
    try {
      const res = await axios.get(`${API}/agents/campaigns/${campaignId}`);
      setCampaignDetail(res.data);
    } catch { /* campaign detail error */ }
  };

  const saveCampaignEdit = async () => {
    if (!editingCampaign) return;
    try {
      await axios.patch(`${API}/agents/campaigns/${editingCampaign.id}`, {
        name: editingCampaign.name,
        goal: editingCampaign.goal,
      });
      setEditingCampaign(null);
      loadDashboard();
      if (selectedCampaign === editingCampaign.id) loadCampaignDetail(editingCampaign.id);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save campaign");
    }
  };

  const deleteCampaign = async (campaignId, name) => {
    if (!window.confirm(`Delete campaign "${name}" and ALL its tasks? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/agents/campaigns/${campaignId}`);
      loadDashboard();
      if (tab === "campaign-detail") setTab("campaigns");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete campaign");
    }
  };

  const archiveCampaign = async (campaignId) => {
    try {
      await axios.patch(`${API}/agents/campaigns/${campaignId}`, { status: "archived" });
      loadDashboard();
      if (selectedCampaign === campaignId) loadCampaignDetail(campaignId);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to archive");
    }
  };

  const createPlan = async () => {
    if (!planGoal.trim()) return;
    setPlanLoading(true);
    setPlanResult(null);
    setPlanProgress("Starting Director…");
    try {
      // Start async job
      const startRes = await axios.post(`${API}/agents/director/plan`, {
        goal: planGoal, channels: planChannels, timeframe: planTimeframe
      });
      const jobId = startRes.data.job_id;
      if (!jobId) throw new Error("No job id returned");

      setPlanProgress("Director is thinking…");

      // Poll status — Director plans take 20-45s
      const maxAttempts = 90; // 90 × 2s = 180s max wait
      let attempt = 0;
      const startAt = Date.now();
      while (attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, 2000));
        attempt += 1;
        const elapsed = Math.round((Date.now() - startAt) / 1000);
        try {
          const statusRes = await axios.get(`${API}/agents/director/plan/status/${jobId}`);
          const st = statusRes.data.status;
          if (st === "completed") {
            setPlanProgress("");
            setPlanResult(statusRes.data);
            loadDashboard();
            setPlanLoading(false);
            return;
          }
          if (st === "failed") {
            setPlanProgress("");
            alert(statusRes.data.error || "Plan creation failed");
            setPlanLoading(false);
            return;
          }
          // else "pending" or "running" — show progress
          if (st === "running") {
            setPlanProgress(`Director is drafting your plan… (${elapsed}s)`);
          } else {
            setPlanProgress(`Queued… (${elapsed}s)`);
          }
        } catch {
          // Transient network error, keep polling
          setPlanProgress(`Reconnecting… (${elapsed}s)`);
        }
      }
      alert("Plan creation timed out. The task may still be running — check Campaigns tab shortly.");
    } catch (err) {
      alert(err.response?.data?.detail || err.message || "Failed to create plan");
    }
    setPlanLoading(false);
    setPlanProgress("");
  };

  const executeTask = async (taskId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/agents/tasks/execute/${taskId}`);
      if (res.data.task) {
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...res.data.task } : t));
        if (selectedTask?.id === taskId) setSelectedTask(res.data.task);
      }
      loadDashboard();
    } catch (err) { alert("Execution failed"); }
    setLoading(false);
  };

  const executeAllTasks = async (campaignId) => {
    setExecutingAll(true);
    try {
      const res = await axios.post(`${API}/agents/tasks/execute-all/${campaignId}`);
      alert(`${res.data.executing} tasks running in background. Refresh to see progress.`);
      const interval = setInterval(() => { loadDashboard(); }, 10000);
      setTimeout(() => { clearInterval(interval); setExecutingAll(false); }, 120000);
      loadDashboard();
    } catch (err) { alert("Failed to start execution"); setExecutingAll(false); }
  };

  const reviewTask = async (taskId, action, feedback = "") => {
    try {
      const res = await axios.post(`${API}/agents/tasks/review`, {
        task_id: taskId, action, feedback
      });
      if (res.data.output) {
        setSelectedTask(prev => prev ? { ...prev, output: res.data.output, status: "review" } : null);
      }
      loadDashboard();
      if (action === "approve") setSelectedTask(null);
    } catch (err) { alert("Review failed"); }
  };

  const chatWithAgent = async () => {
    if (!chatPrompt.trim()) return;
    setChatLoading(true);
    setChatResponse(null);
    try {
      const res = await axios.post(`${API}/agents/chat`, {
        agent_type: chatAgent, prompt: chatPrompt
      });
      setChatResponse(res.data);
      loadDashboard();
    } catch (err) {
      setChatResponse({ error: err.response?.data?.detail || "Chat failed" });
    }
    setChatLoading(false);
  };

  /* ─── LinkedIn ─── */
  const loadLinkedIn = useCallback(async () => {
    try {
      const [statusRes, postsRes, summaryRes, orgsRes] = await Promise.all([
        axios.get(`${API}/linkedin/status`),
        axios.get(`${API}/linkedin/posts`).catch(() => ({ data: { posts: [] } })),
        axios.get(`${API}/linkedin/analytics/summary`).catch(() => ({ data: null })),
        axios.get(`${API}/linkedin/organizations`).catch(() => ({ data: { organizations: [], api_available: false } })),
      ]);
      setLinkedinStatus(statusRes.data);
      setLinkedinPosts(postsRes.data.posts || []);
      setLinkedinSummary(summaryRes.data);
      setLinkedinOrgs(orgsRes.data || { organizations: [], api_available: false });
    } catch {
      /* linkedin load error */
    }
  }, []);

  useEffect(() => { loadLinkedIn(); }, [loadLinkedIn]);

  // Handle OAuth redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("linkedin_connected")) {
      setLinkedinBanner({ type: "success", msg: "LinkedIn connected successfully!" });
      setTab("linkedin");
      window.history.replaceState({}, "", window.location.pathname);
      loadLinkedIn();
    } else if (params.get("linkedin_error")) {
      setLinkedinBanner({ type: "error", msg: `LinkedIn: ${params.get("linkedin_error")}` });
      setTab("linkedin");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadLinkedIn]);

  const connectLinkedIn = async (mode = "personal") => {
    setLinkedinLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.get(`${API}/linkedin/connect?origin=${encodeURIComponent(origin)}&mode=${mode}`);
      window.location.href = res.data.authorization_url;
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to start LinkedIn auth");
      setLinkedinLoading(false);
    }
  };

  const disconnectLinkedIn = async () => {
    if (!window.confirm("Disconnect LinkedIn? You can reconnect anytime.")) return;
    try {
      await axios.post(`${API}/linkedin/disconnect`);
      setLinkedinStatus({ connected: false });
      setLinkedinPosts([]);
      setLinkedinSummary(null);
      setLinkedinBanner({ type: "success", msg: "LinkedIn disconnected." });
    } catch {
      alert("Failed to disconnect");
    }
  };

  const publishToLinkedIn = async (text, taskId = null, target = "personal") => {
    setPublishing(true);
    try {
      const res = await axios.post(`${API}/linkedin/publish`, { text, task_id: taskId, target });
      setLinkedinBanner({
        type: "success",
        msg: `Published! ${res.data.post_url ? `View: ${res.data.post_url}` : ""}`
      });
      if (!taskId) setNewPostText("");
      loadLinkedIn();
      return res.data;
    } catch (err) {
      setLinkedinBanner({
        type: "error",
        msg: err.response?.data?.detail || "Publish failed"
      });
      return null;
    } finally {
      setPublishing(false);
    }
  };

  const refreshPostAnalytics = async (postId) => {
    setRefreshingPostId(postId);
    try {
      const res = await axios.post(`${API}/linkedin/posts/${postId}/refresh-analytics`);
      setLinkedinPosts(prev => prev.map(p => p.id === postId ? { ...p, analytics: res.data.analytics } : p));
      if (res.data.note && !res.data.community_api_active) {
        setLinkedinBanner({ type: "info", msg: res.data.note });
      }
    } catch {
      alert("Failed to refresh analytics");
    }
    setRefreshingPostId(null);
  };

  /* ─── Shared: Task row with expand ─── */
  const TaskRow = ({ task, showAgent = true, selected = false, onSelect = null, showCheckbox = false }) => {
    const agent = AGENTS[task.agent_type] || { color: C.textMuted, name: task.agent_type };
    const isExpanded = selectedTask?.id === task.id;
    const Icon = AGENT_ICONS[task.agent_type] || Target;

    return (
      <Card
        className="overflow-hidden transition-all duration-200 cursor-pointer"
        glow={isExpanded ? C.primary : (selected ? C.primary : null)}
        onClick={() => setSelectedTask(isExpanded ? null : task)}
        data-testid={`task-row-${task.id}`}
      >
        <div className="p-3.5 sm:p-4">
          <div className="flex items-start gap-3">
            {showCheckbox && (
              <button
                onClick={e => { e.stopPropagation(); onSelect && onSelect(); }}
                className="flex-shrink-0 pt-1"
                data-testid={`select-task-${task.id}`}
              >
                {selected
                  ? <CheckSquare size={16} style={{ color: C.primary }} />
                  : <Square size={16} style={{ color: C.textDim }} />}
              </button>
            )}
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${agent.color}18` }}>
              <Icon size={15} style={{ color: agent.color }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium break-words" style={{ color: C.text }}>{task.title}</p>
              {showAgent && <p className="text-xs mt-0.5" style={{ color: agent.color }}>{agent.name}</p>}
            </div>
            <ChevronDown
              size={16}
              className="transition-transform duration-200 flex-shrink-0 mt-1"
              style={{ color: C.textDim, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <StatusPill status={task.status} />
            {task.status === "pending" && (
              <button
                onClick={e => { e.stopPropagation(); executeTask(task.id); }}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1"
                style={{ background: `${C.secondary}18`, color: C.secondary }}
                data-testid={`execute-task-${task.id}`}
              >
                <Play size={12} />Execute
              </button>
            )}
            {task.published_to_linkedin && (
              <span className="px-2 py-1 rounded-lg text-[11px] font-medium flex items-center gap-1" style={{ background: "#0A66C215", color: "#0A66C2" }}>
                <Linkedin size={11} />Published
              </span>
            )}
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              {...expandCollapse}
              className="overflow-hidden"
            >
              <div className="px-3.5 sm:px-4 pb-4 pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
                {/* Task brief (always visible when expanded) */}
                {task.prompt && (
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider font-medium mb-1.5" style={{ color: C.textDim }}>Agent Brief</p>
                    <div className="text-xs leading-relaxed rounded-lg p-3 whitespace-pre-wrap" style={{ color: `${C.text}99`, background: C.bg, border: `1px solid ${C.border}` }}>
                      {task.prompt}
                    </div>
                  </div>
                )}

                {/* Output — only if executed */}
                {task.output ? (
                  <>
                    <p className="text-[10px] uppercase tracking-wider font-medium mb-1.5" style={{ color: C.textDim }}>Output</p>
                    <div
                      className="max-h-96 overflow-y-auto mb-4 rounded-xl p-4"
                      style={{ background: C.bg }}
                    >
                      <MDContent>{task.output}</MDContent>
                    </div>
                  </>
                ) : (
                  <div className="mb-4 p-4 rounded-xl flex items-center gap-3" style={{ background: C.bg, border: `1px dashed ${C.border}` }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${C.secondary}15` }}>
                      <Play size={14} style={{ color: C.secondary }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: C.text }}>No output yet</p>
                      <p className="text-xs mt-0.5" style={{ color: C.textDim }}>
                        This task is {task.status}. Click <strong>Execute</strong> to run the agent.
                      </p>
                    </div>
                    {task.status === "pending" && (
                      <button
                        onClick={e => { e.stopPropagation(); executeTask(task.id); }}
                        className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1 flex-shrink-0"
                        style={{ background: `${C.secondary}20`, color: C.secondary }}
                      >
                        <Play size={12} />Execute
                      </button>
                    )}
                  </div>
                )}

                {task.status === "review" && (
                  <div className="flex flex-col gap-3 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
                    <div className="flex gap-2">
                      <button
                        onClick={e => { e.stopPropagation(); reviewTask(task.id, "approve"); }}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
                        style={{ background: `${C.success}18`, color: C.success }}
                        data-testid={`approve-task-${task.id}`}
                      >
                        <CheckCircle2 size={14} />Approve
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); reviewTask(task.id, "reject"); }}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
                        style={{ background: `${C.error}18`, color: C.error }}
                        data-testid={`reject-task-${task.id}`}
                      >
                        <XCircle size={14} />Reject
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <input
                        value={reviewFeedback}
                        onChange={e => setReviewFeedback(e.target.value)}
                        placeholder="Revision feedback..."
                        className="flex-1 rounded-lg px-3 py-2 text-sm outline-none transition-colors"
                        style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
                        onClick={e => e.stopPropagation()}
                      />
                      <button
                        onClick={e => { e.stopPropagation(); reviewTask(task.id, "revise", reviewFeedback); setReviewFeedback(""); }}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
                        style={{ background: `${C.warning}18`, color: C.warning }}
                      >
                        <RefreshCw size={14} />Revise
                      </button>
                    </div>
                  </div>
                )}

                {/* Publish to LinkedIn — for social_media agent approved tasks */}
                {task.agent_type === "social_media" && (task.status === "approved" || task.status === "review") && !task.published_to_linkedin && (
                  <div className="flex flex-wrap items-center gap-2 pt-3 mt-3" style={{ borderTop: `1px solid ${C.border}` }}>
                    {linkedinStatus.connected ? (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setPublishModal({
                            task_id: task.id,
                            text: extractPostText(task.output),
                            target: postTarget,
                            phase: "preview",
                          });
                        }}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
                        style={{ background: "#0A66C215", color: "#0A66C2", border: `1px solid #0A66C240` }}
                        data-testid={`publish-linkedin-${task.id}`}
                      >
                        <Linkedin size={14} />
                        Publish to LinkedIn...
                      </button>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); setTab("linkedin"); }}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
                        style={{ background: C.surface, color: C.textMuted, border: `1px solid ${C.border}` }}
                      >
                        <Linkedin size={14} />Connect LinkedIn to publish
                      </button>
                    )}
                  </div>
                )}

                {task.published_to_linkedin && (
                  <div className="pt-3 mt-3 flex items-center gap-2 text-xs" style={{ borderTop: `1px solid ${C.border}`, color: C.success }}>
                    <CheckCircle2 size={14} />
                    <span>Published to LinkedIn</span>
                    {task.linkedin_post_url && (
                      <a
                        href={task.linkedin_post_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline flex items-center gap-1 ml-auto"
                        style={{ color: "#0A66C2" }}
                        onClick={e => e.stopPropagation()}
                      >
                        <ExternalLink size={12} />View post
                      </a>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: C.bg, fontFamily: "'Inter', sans-serif" }}>
      {/* ─── CSS Animations ─── */}
      <style>{`
        @keyframes goldPulse { 0%,100%{box-shadow:0 0 15px ${C.primary}10} 50%{box-shadow:0 0 30px ${C.primary}25} }
        @keyframes gradientLine { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        .gold-pulse { animation: goldPulse 3s ease-in-out infinite; }
        .gradient-line { background: linear-gradient(90deg, transparent, ${C.secondary}60, transparent); background-size: 200% 100%; animation: gradientLine 2s linear infinite; height: 2px; }
      `}</style>

      {/* ═══ HEADER ═══ */}
      <div style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5 md:gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center gold-pulse flex-shrink-0" style={{ background: `${C.primary}15`, border: `1px solid ${C.primary}30` }}>
              <Sparkles size={18} style={{ color: C.primary }} />
            </div>
            <div className="min-w-0">
              <h1 className="text-base md:text-lg font-bold truncate" style={{ color: C.text }}>AI Marketing Agents</h1>
              <p className="text-[11px] md:text-xs truncate" style={{ color: C.textDim }}>Autonomous marketing team powered by AI</p>
            </div>
          </div>
          <div className="flex items-center gap-3 md:gap-4">
            <span className="text-xs hidden sm:inline" style={{ color: C.textMuted }}>{user?.name || user?.email}</span>
            <button
              onClick={onLogout}
              className="p-2 rounded-lg transition-colors hidden md:inline-flex"
              style={{ color: C.textDim }}
              data-testid="logout-btn"
            >
              <LogOut size={16} />
            </button>
            {/* Mobile burger */}
            <button
              onClick={() => setMobileNavOpen(true)}
              className="p-2 rounded-lg transition-colors md:hidden"
              style={{ color: C.text, background: `${C.primary}14`, border: `1px solid ${C.primary}30` }}
              data-testid="mobile-menu-open-btn"
              aria-label="Open navigation menu"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* ═══ DESKTOP TABS (md and up) ═══ */}
      <div className="hidden md:block" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map(t => {
            const active = tab === t.id;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className="px-4 py-3 text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors"
                style={{
                  borderBottom: `2px solid ${active ? C.primary : 'transparent'}`,
                  color: active ? C.primary : C.textDim,
                }}
                data-testid={`tab-${t.id}`}
              >
                <Icon size={15} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ MOBILE NAVIGATION DRAWER ═══ */}
      <AnimatePresence>
        {mobileNavOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileNavOpen(false)}
              className="fixed inset-0 z-40 md:hidden"
              style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
              data-testid="mobile-nav-overlay"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
              className="fixed top-0 right-0 bottom-0 z-50 w-72 max-w-[85vw] flex flex-col md:hidden"
              style={{ background: C.bg, borderLeft: `1px solid ${C.border}` }}
              data-testid="mobile-nav-drawer"
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${C.border}` }}>
                <div className="flex items-center gap-2">
                  <Sparkles size={16} style={{ color: C.primary }} />
                  <span className="text-sm font-semibold" style={{ color: C.text }}>Navigation</span>
                </div>
                <button
                  onClick={() => setMobileNavOpen(false)}
                  className="p-1.5 rounded-lg"
                  style={{ color: C.textDim }}
                  data-testid="mobile-nav-close-btn"
                  aria-label="Close navigation"
                >
                  <CloseIcon size={18} />
                </button>
              </div>

              {/* User badge */}
              {user && (
                <div className="px-5 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textDim }}>Signed in as</p>
                  <p className="text-sm truncate mt-0.5" style={{ color: C.text }}>{user.name || user.email}</p>
                </div>
              )}

              {/* Tab items */}
              <div className="flex-1 overflow-y-auto py-2">
                {TABS.map(t => {
                  const active = tab === t.id;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => { setTab(t.id); setMobileNavOpen(false); }}
                      className="w-full px-5 py-3 flex items-center gap-3 text-sm font-medium transition-colors"
                      style={{
                        color: active ? C.primary : C.text,
                        background: active ? `${C.primary}10` : "transparent",
                        borderLeft: `3px solid ${active ? C.primary : 'transparent'}`,
                      }}
                      data-testid={`mobile-tab-${t.id}`}
                    >
                      <Icon size={16} style={{ color: active ? C.primary : C.textDim }} />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {/* Logout */}
              <button
                onClick={() => { setMobileNavOpen(false); onLogout(); }}
                className="px-5 py-4 flex items-center gap-3 text-sm font-medium"
                style={{
                  color: C.error,
                  borderTop: `1px solid ${C.border}`,
                }}
                data-testid="mobile-logout-btn"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ═══ CONTENT ═══ */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 md:py-8">
        <AnimatePresence mode="wait">
          {/* ──── DASHBOARD TAB ──── */}
          {tab === "dashboard" && (
            <motion.div key="dashboard" initial={fadeIn.initial} animate={fadeIn.animate} exit={fadeIn.exit}>
              {stats && (
                <>
                  {/* Stats grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {[
                      { label: "Campaigns", value: stats.campaigns.total, sub: `${stats.campaigns.active} active`, color: C.secondary },
                      { label: "Total Tasks", value: stats.tasks.total, color: C.textMuted },
                      { label: "Awaiting Review", value: stats.tasks.in_review, color: C.warning, highlight: true },
                      { label: "Approved", value: stats.tasks.approved, color: C.success },
                    ].map((s, i) => (
                      <motion.div key={s.label} {...fadeInUp(i * 0.05)}>
                        <Card className="p-5" glow={s.highlight ? C.warning : null}>
                          <p className="text-[11px] uppercase tracking-wider font-medium" style={{ color: C.textDim }}>{s.label}</p>
                          <p className="text-3xl font-bold mt-1" style={{ color: s.highlight ? s.color : C.text }}>{s.value}</p>
                          {s.sub && <p className="text-xs mt-1" style={{ color: C.textDim }}>{s.sub}</p>}
                        </Card>
                      </motion.div>
                    ))}
                  </div>

                  {/* Per-Agent Analytics */}
                  <h2 className="text-base font-semibold mb-4" style={{ color: C.text }}>Your AI Team — Performance</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
                    {Object.entries(AGENTS).map(([type, agent], i) => {
                      const Icon = AGENT_ICONS[type] || Target;
                      const data = perAgent.find(p => p.agent_type === type) || { total: 0, approved: 0, review: 0, pending: 0, published: 0 };
                      const successPct = data.total > 0 ? Math.round((data.approved / data.total) * 100) : 0;
                      return (
                        <motion.div key={type} {...fadeInUp(0.1 + i * 0.04)}>
                          <Card className="p-4" style={{ borderColor: `${agent.color}25` }}>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${agent.color}15` }}>
                                <Icon size={18} style={{ color: agent.color }} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate" style={{ color: agent.color }}>{agent.name}</p>
                                <p className="text-xs" style={{ color: C.textDim }}>{data.total} total</p>
                              </div>
                              {type === "social_media" && data.published > 0 && (
                                <span className="text-xs flex items-center gap-1" style={{ color: "#0A66C2" }}>
                                  <Linkedin size={11} />{data.published}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-4 gap-1 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.textDim }}>
                              <div className="text-center">
                                <p className="text-base font-bold" style={{ color: C.success }}>{data.approved}</p>
                                <p>OK</p>
                              </div>
                              <div className="text-center">
                                <p className="text-base font-bold" style={{ color: C.warning }}>{data.review}</p>
                                <p>Review</p>
                              </div>
                              <div className="text-center">
                                <p className="text-base font-bold" style={{ color: C.secondary }}>{data.pending}</p>
                                <p>Pending</p>
                              </div>
                              <div className="text-center">
                                <p className="text-base font-bold" style={{ color: C.primary }}>{successPct}%</p>
                                <p>Success</p>
                              </div>
                            </div>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Engagement Over Time Chart */}
                  {engagementTimeline.length > 0 && (
                    <>
                      <h2 className="text-base font-semibold mb-4" style={{ color: C.text }}>Engagement Over Time</h2>
                      <Card className="p-5 mb-8">
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart data={engagementTimeline} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="day" tick={{ fill: C.textDim, fontSize: 11 }} axisLine={{ stroke: C.border }} tickLine={false} />
                            <YAxis tick={{ fill: C.textDim, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip
                              contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }}
                              itemStyle={{ color: C.text }}
                            />
                            <Line type="monotone" dataKey="impressions" stroke={C.secondary} strokeWidth={2} dot={{ r: 3, fill: C.secondary }} name="Impressions" />
                            <Line type="monotone" dataKey="likes" stroke={C.error} strokeWidth={2} dot={{ r: 3, fill: C.error }} name="Likes" />
                            <Line type="monotone" dataKey="comments" stroke={C.primary} strokeWidth={2} dot={{ r: 3, fill: C.primary }} name="Comments" />
                            <Line type="monotone" dataKey="posts" stroke={C.success} strokeWidth={2} dot={{ r: 3, fill: C.success }} name="Posts" />
                          </LineChart>
                        </ResponsiveContainer>
                      </Card>
                    </>
                  )}

                  {/* Publishing Queue: Ready to Publish */}
                  {readyToPublish.length > 0 && (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-base font-semibold" style={{ color: C.text }}>
                          Ready to Publish <span className="text-sm font-normal" style={{ color: C.textDim }}>({readyToPublish.length})</span>
                        </h2>
                        <button
                          onClick={() => setTab("linkedin")}
                          className="text-xs flex items-center gap-1"
                          style={{ color: "#0A66C2" }}
                        >
                          Go to LinkedIn →
                        </button>
                      </div>
                      <div className="space-y-2 mb-8">
                        {readyToPublish.slice(0, 5).map(task => (
                          <Card key={task.id} className="p-4 flex items-center justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${C.success}15` }}>
                                <CheckCircle2 size={14} style={{ color: C.success }} />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate" style={{ color: C.text }}>{task.title}</p>
                                <p className="text-xs" style={{ color: C.textDim }}>
                                  Approved {task.approved_at ? new Date(task.approved_at).toLocaleDateString() : "recently"} · Not yet published
                                </p>
                              </div>
                            </div>
                            <span className="px-2.5 py-1 rounded-lg text-[11px] font-medium flex-shrink-0" style={{ background: `${C.warning}15`, color: C.warning }}>
                              Awaiting publish
                            </span>
                          </Card>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Recently Published */}
                  {recentlyPublished.length > 0 && (
                    <>
                      <h2 className="text-base font-semibold mb-4" style={{ color: C.text }}>
                        Recently Published <span className="text-sm font-normal" style={{ color: C.textDim }}>({recentlyPublished.length})</span>
                      </h2>
                      <div className="space-y-2 mb-8">
                        {recentlyPublished.slice(0, 5).map(task => (
                          <Card key={task.id} className="p-4 flex items-center justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#0A66C215" }}>
                                <Linkedin size={14} style={{ color: "#0A66C2" }} />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate" style={{ color: C.text }}>{task.title}</p>
                                <p className="text-xs" style={{ color: C.textDim }}>
                                  Published {task.published_at ? new Date(task.published_at).toLocaleString() : ""}
                                </p>
                              </div>
                            </div>
                            {task.linkedin_post_url && (
                              <a
                                href={task.linkedin_post_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs flex items-center gap-1 flex-shrink-0"
                                style={{ color: "#0A66C2" }}
                              >
                                <ExternalLink size={12} />View
                              </a>
                            )}
                          </Card>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Awaiting Review */}
                  {stats.tasks.in_review > 0 && (
                    <>
                      <h2 className="text-base font-semibold mb-4" style={{ color: C.text }}>Awaiting Your Review</h2>
                      <div className="space-y-2">
                        {reviewTasks.map(task => (
                          <Card key={task.id} className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-2 rounded-full" style={{ background: AGENTS[task.agent_type]?.color || C.textDim }} />
                              <div>
                                <p className="text-sm font-medium" style={{ color: C.text }}>{task.title}</p>
                                <p className="text-xs" style={{ color: AGENTS[task.agent_type]?.color }}>{AGENTS[task.agent_type]?.name}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => { setSelectedTask(task); setTab("tasks"); }}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                              style={{ background: `${C.warning}18`, color: C.warning }}
                            >
                              Review
                            </button>
                          </Card>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </motion.div>
          )}

          {/* ──── DIRECTOR TAB ──── */}
          {tab === "director" && (
            <motion.div key="director" initial={fadeIn.initial} animate={fadeIn.animate} exit={fadeIn.exit}>
              {/* Director Panel - Signature element */}
              <Card className="p-6 mb-6 gold-pulse" glow={C.primary}>
                <div className="flex items-center gap-3 mb-1">
                  <Crown size={20} style={{ color: C.primary }} />
                  <h2 className="text-lg font-semibold" style={{ color: C.text }}>Marketing Director</h2>
                </div>
                <p className="text-sm" style={{ color: C.textMuted }}>Create a campaign plan and your AI team will execute it</p>
              </Card>

              <div className="max-w-2xl space-y-5">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-medium mb-2" style={{ color: C.textMuted }}>Campaign Goal</label>
                  <textarea
                    value={planGoal}
                    onChange={e => setPlanGoal(e.target.value)}
                    placeholder="e.g., Get 1000 new signups in 2 weeks, Launch LinkedIn campaign for professionals..."
                    className="w-full rounded-xl p-4 text-sm resize-none h-24 outline-none transition-colors"
                    style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text }}
                    onFocus={e => e.target.style.borderColor = `${C.primary}60`}
                    onBlur={e => e.target.style.borderColor = C.border}
                    data-testid="plan-goal-input"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider font-medium mb-2" style={{ color: C.textMuted }}>Channels</label>
                  <div className="flex flex-wrap gap-2">
                    {CHANNELS.map(ch => {
                      const selected = planChannels.includes(ch);
                      return (
                        <button
                          key={ch}
                          onClick={() => setPlanChannels(prev => selected ? prev.filter(c => c !== ch) : [...prev, ch])}
                          className="px-3.5 py-2 rounded-lg text-xs font-medium transition-all duration-200"
                          style={{
                            background: selected ? `${C.primary}15` : C.surface,
                            color: selected ? C.primary : C.textDim,
                            border: `1px solid ${selected ? `${C.primary}40` : C.border}`,
                          }}
                        >
                          {ch.charAt(0).toUpperCase() + ch.slice(1)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider font-medium mb-2" style={{ color: C.textMuted }}>Timeframe</label>
                  <div className="flex gap-2">
                    {TIMEFRAMES.map(tf => {
                      const selected = planTimeframe === tf;
                      return (
                        <button
                          key={tf}
                          onClick={() => setPlanTimeframe(tf)}
                          className="px-3.5 py-2 rounded-lg text-xs font-medium transition-all duration-200"
                          style={{
                            background: selected ? `${C.primary}15` : C.surface,
                            color: selected ? C.primary : C.textDim,
                            border: `1px solid ${selected ? `${C.primary}40` : C.border}`,
                          }}
                        >
                          {tf}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button
                  onClick={createPlan}
                  disabled={planLoading || !planGoal.trim()}
                  className="px-6 py-3 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2 disabled:opacity-40"
                  style={{
                    background: C.primary,
                    color: '#000',
                    boxShadow: `0 0 20px ${C.primary}30`,
                  }}
                  data-testid="create-plan-btn"
                >
                  {planLoading ? (
                    <><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />{planProgress || "Director is planning..."}</>
                  ) : (
                    <><Sparkles size={16} />Create Marketing Plan</>
                  )}
                </button>
              </div>

              {/* Plan result */}
              {planResult && (
                <motion.div {...fadeInUp()} className="mt-8">
                  <Card className="p-5" glow={C.primary}>
                    <h3 className="text-base md:text-lg font-semibold mb-4 break-words" style={{ color: C.primary }}>
                      Campaign: {planResult.campaign?.name}
                    </h3>
                    <div className="mb-6 rounded-xl p-4 max-h-96 overflow-y-auto" style={{ background: C.bg }}>
                      <MDContent>{planResult.plan}</MDContent>
                    </div>

                    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                      <h4 className="font-medium text-sm" style={{ color: C.text }}>{planResult.tasks?.length || 0} Tasks Created</h4>
                      <button
                        onClick={() => executeAllTasks(planResult.campaign?.id)}
                        disabled={executingAll}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 disabled:opacity-50 whitespace-nowrap"
                        style={{ background: `${C.success}18`, color: C.success }}
                        data-testid="execute-all-plan-btn"
                      >
                        <Play size={14} />{executingAll ? "Executing..." : "Execute All Tasks"}
                      </button>
                    </div>

                    <div className="space-y-2">
                      {(planResult.tasks || []).map(task => (
                        <TaskRow key={task.id} task={task} />
                      ))}
                    </div>
                  </Card>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ──── CHAT TAB ──── */}
          {tab === "chat" && (
            <motion.div key="chat" initial={fadeIn.initial} animate={fadeIn.animate} exit={fadeIn.exit} className="max-w-2xl">
              <h2 className="text-lg font-semibold mb-6" style={{ color: C.text }}>Chat with Any Agent</h2>

              <div className="mb-5">
                <label className="block text-xs uppercase tracking-wider font-medium mb-2" style={{ color: C.textMuted }}>Select Agent</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(AGENTS).map(([type, agent]) => {
                    const Icon = AGENT_ICONS[type] || Target;
                    const active = chatAgent === type;
                    return (
                      <button
                        key={type}
                        onClick={() => setChatAgent(type)}
                        className="p-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2"
                        style={{
                          background: active ? `${agent.color}12` : C.surface,
                          color: active ? agent.color : C.textDim,
                          border: `1px solid ${active ? `${agent.color}40` : C.border}`,
                          boxShadow: active ? `0 0 12px ${agent.color}15` : 'none',
                        }}
                      >
                        <Icon size={15} />{agent.short}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-4">
                <textarea
                  value={chatPrompt}
                  onChange={e => setChatPrompt(e.target.value)}
                  placeholder="Ask anything... e.g., 'Write 5 Twitter posts promoting our LinkedIn headshot feature'"
                  className="w-full rounded-xl p-4 text-sm resize-none h-32 outline-none transition-colors"
                  style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text }}
                  onFocus={e => e.target.style.borderColor = `${C.primary}60`}
                  onBlur={e => e.target.style.borderColor = C.border}
                  data-testid="chat-prompt-input"
                />
              </div>

              <button
                onClick={chatWithAgent}
                disabled={chatLoading || !chatPrompt.trim()}
                className="px-6 py-3 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2 disabled:opacity-40"
                style={{ background: C.primary, color: '#000', boxShadow: `0 0 20px ${C.primary}30` }}
                data-testid="chat-send-btn"
              >
                {chatLoading ? (
                  <><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />Agent thinking...</>
                ) : (
                  <><Send size={16} />Send to Agent</>
                )}
              </button>

              {chatLoading && <div className="gradient-line mt-4 rounded-full" />}

              {chatResponse && (
                <motion.div {...fadeInUp()} className="mt-6">
                  <Card className="p-5">
                    <p className="text-xs font-medium mb-3" style={{ color: AGENTS[chatAgent]?.color }}>{chatResponse.agent}</p>
                    <div data-testid="chat-response">
                      {chatResponse.error
                        ? <p className="text-sm" style={{ color: C.error }}>{chatResponse.error}</p>
                        : <MDContent>{chatResponse.output}</MDContent>}
                    </div>
                  </Card>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ──── ALL TASKS TAB ──── */}
          {tab === "tasks" && (
            <motion.div key="tasks" initial={fadeIn.initial} animate={fadeIn.animate} exit={fadeIn.exit}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="text-lg font-semibold" style={{ color: C.text }}>
                  All Tasks <span className="text-sm font-normal" style={{ color: C.textDim }}>({filteredTasks.length} of {tasks.length})</span>
                </h2>
                <button
                  onClick={loadDashboard}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                  style={{ color: C.textDim, background: C.surface, border: `1px solid ${C.border}` }}
                >
                  <RefreshCw size={12} />Refresh
                </button>
              </div>

              {/* Filter toolbar */}
              <Card className="p-3 mb-4">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Search */}
                  <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <Search size={14} style={{ color: C.textDim }} />
                    <input
                      value={taskSearch}
                      onChange={e => setTaskSearch(e.target.value)}
                      placeholder="Search tasks..."
                      className="flex-1 bg-transparent outline-none text-sm"
                      style={{ color: C.text }}
                      data-testid="task-search-input"
                    />
                  </div>

                  {/* Status pills */}
                  <div className="flex flex-wrap gap-1.5">
                    {Object.keys(STATUS_LABELS).map(s => (
                      <button
                        key={s}
                        onClick={() => setTaskFilterStatus(s)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5"
                        style={{
                          background: taskFilterStatus === s ? `${C.primary}18` : C.bg,
                          color: taskFilterStatus === s ? C.primary : C.textDim,
                          border: `1px solid ${taskFilterStatus === s ? `${C.primary}40` : C.border}`,
                        }}
                        data-testid={`filter-status-${s}`}
                      >
                        {STATUS_LABELS[s]}
                        {s !== "all" && (
                          <span className="opacity-60">({taskStatusCounts[s] || 0})</span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Agent filter */}
                  <select
                    value={taskFilterAgent}
                    onChange={e => setTaskFilterAgent(e.target.value)}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium outline-none cursor-pointer"
                    style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}` }}
                    data-testid="filter-agent-select"
                  >
                    <option value="all">All agents</option>
                    {Object.keys(AGENTS).map(a => (
                      <option key={a} value={a}>{AGENTS[a].name}</option>
                    ))}
                  </select>
                </div>

                {/* Bulk actions bar */}
                {filteredTasks.length > 0 && (
                  <div className="flex items-center gap-2 mt-3 pt-3 flex-wrap" style={{ borderTop: `1px solid ${C.border}` }}>
                    <button
                      onClick={selectAllFiltered}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
                      style={{ color: C.textDim, background: C.bg, border: `1px solid ${C.border}` }}
                      data-testid="select-all-tasks"
                    >
                      {filteredTasks.every(t => selectedTaskIds.has(t.id)) && filteredTasks.length > 0
                        ? <CheckSquare size={12} /> : <Square size={12} />}
                      {filteredTasks.every(t => selectedTaskIds.has(t.id)) && filteredTasks.length > 0 ? "Deselect all" : "Select all"}
                    </button>
                    {selectedTaskIds.size > 0 && (
                      <>
                        <span className="text-xs" style={{ color: C.textMuted }}>
                          {selectedTaskIds.size} selected
                        </span>
                        <button
                          onClick={() => bulkReviewAction("approve")}
                          disabled={bulkActionLoading}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all disabled:opacity-50"
                          style={{ background: `${C.success}18`, color: C.success }}
                          data-testid="bulk-approve"
                        >
                          <CheckCircle2 size={12} />Approve
                        </button>
                        <button
                          onClick={() => bulkReviewAction("reject")}
                          disabled={bulkActionLoading}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all disabled:opacity-50"
                          style={{ background: `${C.error}18`, color: C.error }}
                          data-testid="bulk-reject"
                        >
                          <XCircle size={12} />Reject
                        </button>
                        <button
                          onClick={() => setSelectedTaskIds(new Set())}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium"
                          style={{ color: C.textDim }}
                        >
                          Clear
                        </button>
                      </>
                    )}
                  </div>
                )}
              </Card>

              <div className="space-y-2">
                {filteredTasks.map(task => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    selected={selectedTaskIds.has(task.id)}
                    onSelect={() => toggleTaskSelection(task.id)}
                    showCheckbox
                  />
                ))}
                {filteredTasks.length === 0 && tasks.length > 0 && (
                  <p className="text-center py-16 text-sm" style={{ color: C.textDim }}>No tasks match these filters.</p>
                )}
                {tasks.length === 0 && (
                  <p className="text-center py-16 text-sm" style={{ color: C.textDim }}>No tasks yet. Use the Director to create a campaign plan.</p>
                )}
              </div>
            </motion.div>
          )}

          {/* ──── CAMPAIGNS TAB ──── */}
          {tab === "campaigns" && (
            <motion.div key="campaigns" initial={fadeIn.initial} animate={fadeIn.animate} exit={fadeIn.exit}>
              <h2 className="text-lg font-semibold mb-6" style={{ color: C.text }}>Campaigns</h2>
              <div className="space-y-3">
                {campaigns.map(camp => {
                  const progress = camp.tasks_total > 0 ? (camp.tasks_completed / camp.tasks_total) * 100 : 0;
                  return (
                    <Card key={camp.id} className="p-5" data-testid={`campaign-card-${camp.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-sm" style={{ color: C.text }}>{camp.name}</h3>
                        <StatusPill status={camp.status} />
                      </div>
                      <p className="text-sm mb-3" style={{ color: C.textMuted }}>{camp.goal}</p>

                      {/* Progress bar */}
                      <div className="h-1.5 rounded-full mb-3 overflow-hidden" style={{ background: C.border }}>
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: C.success }} />
                      </div>

                      <div className="flex items-center gap-4 text-xs mb-4" style={{ color: C.textDim }}>
                        <span>{camp.tasks_completed || 0}/{camp.tasks_total || 0} tasks done</span>
                        {camp.channels?.length > 0 && <span>{camp.channels.join(", ")}</span>}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => { setSelectedCampaign(camp.id); setTab("campaign-detail"); loadCampaignDetail(camp.id); }}
                          className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
                          style={{ background: `${C.primary}15`, color: C.primary, border: `1px solid ${C.primary}30` }}
                          data-testid={`view-results-${camp.id}`}
                        >
                          <Target size={14} />View Results
                        </button>
                        <button
                          onClick={() => executeAllTasks(camp.id)}
                          disabled={executingAll}
                          className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                          style={{ background: C.surface, color: C.textMuted, border: `1px solid ${C.border}` }}
                          data-testid={`execute-pending-${camp.id}`}
                        >
                          {executingAll ? "Running..." : "Execute Pending"}
                        </button>
                        <button
                          onClick={() => setEditingCampaign({ id: camp.id, name: camp.name, goal: camp.goal })}
                          className="px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
                          style={{ background: C.surface, color: C.textDim, border: `1px solid ${C.border}` }}
                          data-testid={`edit-campaign-${camp.id}`}
                          aria-label="Edit campaign"
                        >
                          <Edit2 size={13} />Edit
                        </button>
                        <button
                          onClick={() => deleteCampaign(camp.id, camp.name)}
                          className="px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5"
                          style={{ background: `${C.error}12`, color: C.error, border: `1px solid ${C.error}30` }}
                          data-testid={`delete-campaign-${camp.id}`}
                          aria-label="Delete campaign"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </Card>
                  );
                })}
                {campaigns.length === 0 && (
                  <p className="text-center py-16 text-sm" style={{ color: C.textDim }}>No campaigns yet. Go to Director tab to create one.</p>
                )}
              </div>
            </motion.div>
          )}

          {/* ──── LINKEDIN TAB ──── */}
          {tab === "linkedin" && (
            <motion.div key="linkedin" initial={fadeIn.initial} animate={fadeIn.animate} exit={fadeIn.exit}>
              {linkedinBanner && (
                <motion.div
                  {...fadeInUp()}
                  className="mb-6 p-4 rounded-xl flex items-center justify-between"
                  style={{
                    background: linkedinBanner.type === "error" ? `${C.error}12` : linkedinBanner.type === "info" ? `${C.secondary}12` : `${C.success}12`,
                    border: `1px solid ${linkedinBanner.type === "error" ? C.error : linkedinBanner.type === "info" ? C.secondary : C.success}40`,
                    color: linkedinBanner.type === "error" ? C.error : linkedinBanner.type === "info" ? C.secondary : C.success,
                  }}
                >
                  <span className="text-sm">{linkedinBanner.msg}</span>
                  <button onClick={() => setLinkedinBanner(null)} className="opacity-60 hover:opacity-100">
                    <XCircle size={16} />
                  </button>
                </motion.div>
              )}

              {/* Connection Card */}
              <Card className="p-6 mb-6" glow={linkedinStatus.connected ? "#0A66C2" : null}>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "#0A66C215" }}>
                      <Linkedin size={24} style={{ color: "#0A66C2" }} />
                    </div>
                    {linkedinStatus.connected ? (
                      <div>
                        <p className="text-sm font-semibold" style={{ color: C.text }}>
                          Connected as {linkedinStatus.linkedin_name}
                        </p>
                        <p className="text-xs" style={{ color: C.textDim }}>
                          {linkedinStatus.linkedin_email}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-semibold" style={{ color: C.text }}>LinkedIn not connected</p>
                        <p className="text-xs" style={{ color: C.textDim }}>
                          Connect your LinkedIn to publish posts and track analytics
                        </p>
                      </div>
                    )}
                  </div>
                  {linkedinStatus.connected ? (
                    <button
                      onClick={disconnectLinkedIn}
                      className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                      style={{ background: `${C.error}12`, color: C.error, border: `1px solid ${C.error}30` }}
                      data-testid="linkedin-disconnect-btn"
                    >
                      <Unlink size={14} />Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={connectLinkedIn}
                      disabled={linkedinLoading}
                      className="px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all disabled:opacity-60"
                      style={{ background: "#0A66C2", color: "#fff", boxShadow: "0 0 20px #0A66C230" }}
                      data-testid="linkedin-connect-btn"
                    >
                      <Link2 size={14} />{linkedinLoading ? "Redirecting..." : "Connect LinkedIn"}
                    </button>
                  )}
                </div>
              </Card>

              {linkedinStatus.connected && (
                <>
                  {/* Analytics Summary */}
                  {linkedinSummary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      {[
                        { label: "Total Posts", value: linkedinSummary.total_posts, icon: Share2, color: C.textMuted },
                        { label: "Impressions", value: linkedinSummary.total_impressions, icon: Eye, color: C.secondary },
                        { label: "Engagements", value: linkedinSummary.total_engagements, icon: TrendingUp, color: C.primary },
                        { label: "Avg Eng. Rate", value: `${linkedinSummary.avg_engagement_rate}%`, icon: Heart, color: C.success },
                      ].map((s, i) => {
                        const Icon = s.icon;
                        return (
                          <motion.div key={s.label} {...fadeInUp(i * 0.04)}>
                            <Card className="p-4">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-[11px] uppercase tracking-wider font-medium" style={{ color: C.textDim }}>{s.label}</p>
                                <Icon size={14} style={{ color: s.color }} />
                              </div>
                              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  {/* Quick Composer */}
                  <Card className="p-5 mb-6">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: C.text }}>
                      <PenTool size={14} style={{ color: C.primary }} /> Compose Post
                    </h3>

                    {/* Target selector */}
                    <label className="block text-xs uppercase tracking-wider font-medium mb-2" style={{ color: C.textMuted }}>Post as</label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <button
                        onClick={() => setPostTarget("personal")}
                        className="px-3.5 py-2 rounded-lg text-xs font-medium flex items-center gap-2 transition-all"
                        style={{
                          background: postTarget === "personal" ? `${C.primary}15` : C.bg,
                          color: postTarget === "personal" ? C.primary : C.textDim,
                          border: `1px solid ${postTarget === "personal" ? `${C.primary}40` : C.border}`,
                        }}
                        data-testid="target-personal-btn"
                      >
                        👤 {linkedinStatus.linkedin_name || "Personal profile"}
                      </button>
                      {(linkedinOrgs.organizations || []).map(org => (
                        <button
                          key={org.urn}
                          onClick={() => setPostTarget(org.urn)}
                          className="px-3.5 py-2 rounded-lg text-xs font-medium flex items-center gap-2 transition-all"
                          style={{
                            background: postTarget === org.urn ? "#0A66C215" : C.bg,
                            color: postTarget === org.urn ? "#0A66C2" : C.textDim,
                            border: `1px solid ${postTarget === org.urn ? "#0A66C240" : C.border}`,
                          }}
                          data-testid={`target-org-${org.id}-btn`}
                        >
                          🏢 {org.name}
                        </button>
                      ))}
                      {!linkedinOrgs.api_available && (
                        <button
                          onClick={() => connectLinkedIn("full")}
                          className="px-3.5 py-2 rounded-lg text-xs font-medium flex items-center gap-2 transition-all border-dashed"
                          style={{
                            background: C.bg,
                            color: C.textMuted,
                            border: `1px dashed ${C.border}`,
                          }}
                          title={linkedinOrgs.note || "Enable company posting"}
                          data-testid="enable-company-posting-btn"
                        >
                          🏢 + Enable Company Posting
                        </button>
                      )}
                    </div>
                    {!linkedinOrgs.api_available && linkedinOrgs.note && (
                      <p className="text-xs mb-3" style={{ color: C.textDim }}>ℹ️ {linkedinOrgs.note}</p>
                    )}

                    <textarea
                      value={newPostText}
                      onChange={e => setNewPostText(e.target.value)}
                      placeholder="Write a post to publish directly to your LinkedIn..."
                      maxLength={3000}
                      className="w-full rounded-xl p-4 text-sm resize-none h-32 outline-none"
                      style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
                      data-testid="linkedin-compose-input"
                    />
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs" style={{ color: C.textDim }}>{newPostText.length}/3000</span>
                      <button
                        onClick={() => setPublishModal({
                          task_id: null,
                          text: newPostText,
                          target: postTarget,
                          phase: "preview",
                          fromComposer: true,
                        })}
                        disabled={!newPostText.trim()}
                        className="px-5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
                        style={{ background: "#0A66C2", color: "#fff" }}
                        data-testid="linkedin-publish-btn"
                      >
                        <Send size={13} />Publish to LinkedIn...
                      </button>
                    </div>
                  </Card>

                  {/* Published Posts List */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold" style={{ color: C.text }}>
                      Published Posts ({linkedinPosts.length})
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {linkedinPosts.length === 0 && (
                      <p className="text-center py-12 text-sm" style={{ color: C.textDim }}>
                        No posts published yet. Compose one above or approve a Social Media agent task and publish it.
                      </p>
                    )}
                    {linkedinPosts.map(post => {
                      const a = post.analytics || {};
                      return (
                        <Card key={post.id} className="p-4">
                          <p className="text-sm mb-3 line-clamp-3" style={{ color: C.text }}>{post.text}</p>
                          <div className="flex items-center gap-4 text-xs flex-wrap" style={{ color: C.textDim }}>
                            <span className="flex items-center gap-1"><Eye size={12} />{a.impressions || 0}</span>
                            <span className="flex items-center gap-1"><Heart size={12} />{a.likes || 0}</span>
                            <span className="flex items-center gap-1"><MessageCircle size={12} />{a.comments || 0}</span>
                            <span className="flex items-center gap-1"><Share2 size={12} />{a.shares || 0}</span>
                            {a.engagement_rate > 0 && (
                              <span style={{ color: C.success }}>{a.engagement_rate}% eng</span>
                            )}
                            <span className="ml-auto flex items-center gap-2">
                              {post.post_urn && (
                                <a
                                  href={`https://www.linkedin.com/feed/update/${post.post_urn}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 hover:underline"
                                  style={{ color: "#0A66C2" }}
                                >
                                  <ExternalLink size={12} />View
                                </a>
                              )}
                              <button
                                onClick={() => refreshPostAnalytics(post.id)}
                                disabled={refreshingPostId === post.id}
                                className="flex items-center gap-1 hover:opacity-80 disabled:opacity-50"
                              >
                                <RefreshCw size={12} className={refreshingPostId === post.id ? "animate-spin" : ""} />
                                {refreshingPostId === post.id ? "..." : "Refresh"}
                              </button>
                            </span>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* ──── CAMPAIGN DETAIL TAB ──── */}
          {tab === "campaign-detail" && campaignDetail && (
            <motion.div key="campaign-detail" initial={fadeIn.initial} animate={fadeIn.animate} exit={fadeIn.exit}>
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <button
                  onClick={() => setTab("campaigns")}
                  className="flex items-center gap-1.5 text-sm transition-colors"
                  style={{ color: C.textDim }}
                  data-testid="back-to-campaigns-btn"
                >
                  <ArrowLeft size={16} />Back to Campaigns
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingCampaign({
                      id: campaignDetail.campaign?.id,
                      name: campaignDetail.campaign?.name,
                      goal: campaignDetail.campaign?.goal
                    })}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all"
                    style={{ background: C.surface, color: C.textDim, border: `1px solid ${C.border}` }}
                    data-testid="campaign-detail-edit-btn"
                  >
                    <Edit2 size={12} />Edit
                  </button>
                  {campaignDetail.campaign?.status !== "archived" && (
                    <button
                      onClick={() => archiveCampaign(campaignDetail.campaign?.id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all"
                      style={{ background: C.surface, color: C.textDim, border: `1px solid ${C.border}` }}
                      data-testid="campaign-detail-archive-btn"
                    >
                      Archive
                    </button>
                  )}
                  <button
                    onClick={() => deleteCampaign(campaignDetail.campaign?.id, campaignDetail.campaign?.name)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all"
                    style={{ background: `${C.error}12`, color: C.error, border: `1px solid ${C.error}30` }}
                    data-testid="campaign-detail-delete-btn"
                  >
                    <Trash2 size={12} />Delete
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-semibold" style={{ color: C.text }}>{campaignDetail.campaign?.name}</h2>
                <p className="text-sm mt-1 whitespace-pre-wrap" style={{ color: C.textMuted }}>{campaignDetail.campaign?.goal}</p>
              </div>

              {/* CAMPAIGN ANALYTICS */}
              {(() => {
                const a = campaignDetail.analytics || {};
                const sc = a.status_counts || {};
                const total = sc.total || 0;
                const approved = sc.approved || 0;
                const progressPct = total ? Math.round((approved / total) * 100) : 0;

                return (
                  <>
                    {/* Top KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                      {[
                        { label: "Tasks", value: total, icon: ListChecks, color: C.textMuted },
                        { label: "Approved", value: approved, icon: CheckCircle2, color: C.success },
                        { label: "Published", value: sc.published || 0, icon: Linkedin, color: "#0A66C2" },
                        { label: "Impressions", value: a.total_impressions || 0, icon: Eye, color: C.secondary },
                        { label: "Success Score", value: a.success_score || 0, icon: Award, color: C.primary },
                      ].map((k, i) => {
                        const Icon = k.icon;
                        return (
                          <motion.div key={k.label} {...fadeInUp(i * 0.04)}>
                            <Card className="p-4">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.textDim }}>{k.label}</p>
                                <Icon size={13} style={{ color: k.color }} />
                              </div>
                              <p className="text-xl font-bold" style={{ color: k.color }}>{k.value}</p>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>

                    {/* Progress Bar */}
                    <Card className="p-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs uppercase tracking-wider font-medium" style={{ color: C.textDim }}>Campaign Progress</p>
                        <span className="text-sm font-semibold" style={{ color: C.primary }}>{progressPct}%</span>
                      </div>
                      <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: C.bg }}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${progressPct}%` }}
                          transition={{ duration: 0.6 }}
                          className="h-full rounded-full"
                          style={{ background: `linear-gradient(90deg, ${C.primary}, ${C.secondary})` }}
                        />
                      </div>
                      <div className="flex gap-4 mt-3 text-xs flex-wrap" style={{ color: C.textDim }}>
                        <span>Pending: {sc.pending || 0}</span>
                        <span>In Review: {sc.review || 0}</span>
                        <span style={{ color: C.success }}>Approved: {approved}</span>
                        <span style={{ color: C.error }}>Rejected: {sc.rejected || 0}</span>
                        <span style={{ color: C.error }}>Failed: {sc.failed || 0}</span>
                      </div>
                    </Card>

                    {/* VISUAL CHARTS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {/* Status distribution pie */}
                      <Card className="p-5">
                        <h3 className="text-sm font-semibold mb-3" style={{ color: C.text }}>Task Status Breakdown</h3>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: "Approved", value: sc.approved || 0, color: C.success },
                                { name: "Review", value: sc.review || 0, color: C.warning },
                                { name: "Pending", value: sc.pending || 0, color: C.secondary },
                                { name: "Rejected", value: sc.rejected || 0, color: C.error },
                                { name: "Failed", value: sc.failed || 0, color: C.textMuted },
                              ].filter(d => d.value > 0)}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={80}
                              paddingAngle={2}
                            >
                              {[C.success, C.warning, C.secondary, C.error, C.textMuted].map((color, idx) => (
                                <Cell key={idx} fill={color} stroke={C.bg} strokeWidth={2} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 12 }}
                              itemStyle={{ color: C.text }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex flex-wrap gap-3 justify-center text-xs mt-2">
                          {[
                            { label: "Approved", value: sc.approved || 0, color: C.success },
                            { label: "Review", value: sc.review || 0, color: C.warning },
                            { label: "Pending", value: sc.pending || 0, color: C.secondary },
                            { label: "Rejected", value: sc.rejected || 0, color: C.error },
                          ].filter(d => d.value > 0).map(d => (
                            <span key={d.label} className="flex items-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: d.color }} />
                              <span style={{ color: C.textDim }}>{d.label}: {d.value}</span>
                            </span>
                          ))}
                        </div>
                      </Card>

                      {/* Per-agent bar chart */}
                      <Card className="p-5">
                        <h3 className="text-sm font-semibold mb-3" style={{ color: C.text }}>Tasks Per Agent</h3>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart
                            data={Object.keys(a.by_agent || {}).map(atype => ({
                              agent: (AGENTS[atype]?.name || atype).split(" ")[0],
                              Approved: a.by_agent[atype].approved || 0,
                              Review: a.by_agent[atype].review || 0,
                              Pending: a.by_agent[atype].pending || 0,
                            }))}
                            margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
                          >
                            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="agent" tick={{ fill: C.textDim, fontSize: 11 }} axisLine={{ stroke: C.border }} tickLine={false} />
                            <YAxis tick={{ fill: C.textDim, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip
                              cursor={{ fill: `${C.primary}08` }}
                              contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }}
                              itemStyle={{ color: C.text }}
                            />
                            <Bar dataKey="Approved" stackId="a" fill={C.success} radius={[0, 0, 0, 0]} />
                            <Bar dataKey="Review" stackId="a" fill={C.warning} radius={[0, 0, 0, 0]} />
                            <Bar dataKey="Pending" stackId="a" fill={C.secondary} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    </div>

                    {/* Engagement stats (only if there are published posts) */}
                    {a.linkedin_posts > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        {[
                          { label: "Likes", value: a.total_likes || 0, icon: Heart, color: C.error },
                          { label: "Comments", value: a.total_comments || 0, icon: MessageCircle, color: C.secondary },
                          { label: "Shares", value: a.total_shares || 0, icon: Share2, color: C.primary },
                          { label: "Eng. Rate", value: `${a.avg_engagement_rate || 0}%`, icon: TrendingUp, color: C.success },
                        ].map((k, i) => {
                          const Icon = k.icon;
                          return (
                            <motion.div key={k.label} {...fadeInUp(i * 0.04)}>
                              <Card className="p-3">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textDim }}>{k.label}</p>
                                    <p className="text-lg font-bold" style={{ color: k.color }}>{k.value}</p>
                                  </div>
                                  <Icon size={14} style={{ color: k.color }} />
                                </div>
                              </Card>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}

                    {/* Top Performers */}
                    {(a.top_posts || []).length > 0 && (
                      <Card className="p-4 mb-6">
                        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3" style={{ color: C.text }}>
                          <Award size={14} style={{ color: C.primary }} /> Top Performing Posts
                        </h3>
                        <div className="space-y-2">
                          {a.top_posts.map((post, idx) => (
                            <div key={post.id || idx} className="p-3 rounded-lg flex items-start gap-3" style={{ background: C.bg }}>
                              <span className="text-sm font-bold" style={{ color: C.primary }}>#{idx + 1}</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs line-clamp-2 mb-1" style={{ color: C.text }}>{post.text}</p>
                                <div className="flex gap-3 text-[11px]" style={{ color: C.textDim }}>
                                  <span>❤️ {post.analytics?.likes || 0}</span>
                                  <span>💬 {post.analytics?.comments || 0}</span>
                                  <span>🔄 {post.analytics?.shares || 0}</span>
                                  <span>👁 {post.analytics?.impressions || 0}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}
                  </>
                );
              })()}

              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: C.text }}>
                <ListChecks size={14} />Campaign Tasks
              </h3>
              <div className="space-y-2">
                {(campaignDetail.tasks || []).map(task => (
                  <TaskRow key={task.id} task={task} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ═══ EDIT CAMPAIGN MODAL ═══ */}
      <AnimatePresence>
        {editingCampaign && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setEditingCampaign(null)}
              className="fixed inset-0 z-40"
              style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.18 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-4"
            >
              <div
                className="w-full max-w-2xl rounded-2xl p-6"
                style={{ background: C.surface, border: `1px solid ${C.border}` }}
                data-testid="edit-campaign-modal"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <Edit2 size={16} style={{ color: C.primary }} />
                    <h3 className="text-base font-semibold" style={{ color: C.text }}>Edit Campaign</h3>
                  </div>
                  <button
                    onClick={() => setEditingCampaign(null)}
                    className="p-1.5 rounded-lg"
                    style={{ color: C.textDim }}
                    aria-label="Close"
                  >
                    <CloseIcon size={18} />
                  </button>
                </div>

                <label className="block text-[11px] uppercase tracking-wider font-medium mb-1.5" style={{ color: C.textDim }}>Name</label>
                <input
                  value={editingCampaign.name || ""}
                  onChange={e => setEditingCampaign({ ...editingCampaign, name: e.target.value })}
                  className="w-full rounded-lg px-3 py-2.5 text-sm outline-none mb-4"
                  style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
                  data-testid="edit-campaign-name"
                />

                <label className="block text-[11px] uppercase tracking-wider font-medium mb-1.5" style={{ color: C.textDim }}>Goal / Brief</label>
                <textarea
                  value={editingCampaign.goal || ""}
                  onChange={e => setEditingCampaign({ ...editingCampaign, goal: e.target.value })}
                  rows={10}
                  className="w-full rounded-lg px-3 py-2.5 text-sm outline-none resize-y"
                  style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
                  data-testid="edit-campaign-goal"
                />

                <p className="text-xs mt-2" style={{ color: C.textDim }}>
                  Editing the goal does not re-run the Director. It only updates the stored context for future reference.
                </p>

                <div className="flex justify-end gap-2 mt-5">
                  <button
                    onClick={() => setEditingCampaign(null)}
                    className="px-4 py-2 rounded-lg text-sm font-medium"
                    style={{ color: C.textDim, background: C.bg, border: `1px solid ${C.border}` }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveCampaignEdit}
                    className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5"
                    style={{ background: C.primary, color: "#000" }}
                    data-testid="save-campaign-edit"
                  >
                    <Save size={13} />Save Changes
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* ═══ PUBLISH TO LINKEDIN PREVIEW MODAL ═══ */}
      <AnimatePresence>
        {publishModal && (() => {
          const targetLabel = publishModal.target === "personal"
            ? (linkedinStatus.linkedin_name || "Personal profile")
            : (linkedinOrgs.organizations.find(o => o.urn === publishModal.target)?.name || "Company page");
          const charCount = (publishModal.text || "").length;
          const overLimit = charCount > 3000;

          const doPublish = async () => {
            setPublishModal(m => ({ ...m, phase: "publishing" }));
            try {
              const res = await axios.post(`${API}/linkedin/publish`, {
                text: publishModal.text,
                task_id: publishModal.task_id || undefined,
                target: publishModal.target,
              });
              setPublishModal(m => ({
                ...m,
                phase: "success",
                resultUrl: res.data.post_url || null,
                postUrn: res.data.post_urn || null,
              }));
              if (publishModal.fromComposer) setNewPostText("");
              loadDashboard();
              loadLinkedIn();
            } catch (err) {
              setPublishModal(m => ({
                ...m,
                phase: "failed",
                error: err.response?.data?.detail || err.message || "Publish failed",
              }));
            }
          };

          return (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => publishModal.phase !== "publishing" && setPublishModal(null)}
                className="fixed inset-0 z-40"
                style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.18 }}
                className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto"
              >
                <div
                  className="w-full max-w-5xl rounded-2xl p-6 my-auto"
                  style={{ background: C.surface, border: `1px solid ${C.border}` }}
                  data-testid="publish-preview-modal"
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <Linkedin size={18} style={{ color: "#0A66C2" }} />
                      <h3 className="text-base font-semibold" style={{ color: C.text }}>
                        {publishModal.phase === "preview" && "Review & Publish to LinkedIn"}
                        {publishModal.phase === "publishing" && "Publishing..."}
                        {publishModal.phase === "success" && "Published!"}
                        {publishModal.phase === "failed" && "Publish failed"}
                      </h3>
                    </div>
                    {publishModal.phase !== "publishing" && (
                      <button
                        onClick={() => setPublishModal(null)}
                        className="p-1.5 rounded-lg"
                        style={{ color: C.textDim }}
                        aria-label="Close"
                        data-testid="publish-modal-close"
                      >
                        <CloseIcon size={18} />
                      </button>
                    )}
                  </div>

                  {/* PREVIEW PHASE */}
                  {publishModal.phase === "preview" && (
                    <>
                      {/* Target selector */}
                      <label className="block text-[11px] uppercase tracking-wider font-medium mb-1.5" style={{ color: C.textDim }}>Posting as</label>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <button
                          onClick={() => setPublishModal(m => ({ ...m, target: "personal" }))}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium"
                          style={{
                            background: publishModal.target === "personal" ? `${C.primary}15` : C.bg,
                            color: publishModal.target === "personal" ? C.primary : C.textDim,
                            border: `1px solid ${publishModal.target === "personal" ? `${C.primary}40` : C.border}`,
                          }}
                        >
                          👤 {linkedinStatus.linkedin_name || "Personal profile"}
                        </button>
                        {(linkedinOrgs.organizations || []).map(org => (
                          <button
                            key={org.urn}
                            onClick={() => setPublishModal(m => ({ ...m, target: org.urn }))}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium"
                            style={{
                              background: publishModal.target === org.urn ? "#0A66C215" : C.bg,
                              color: publishModal.target === org.urn ? "#0A66C2" : C.textDim,
                              border: `1px solid ${publishModal.target === org.urn ? "#0A66C240" : C.border}`,
                            }}
                          >
                            🏢 {org.name}
                          </button>
                        ))}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* EDITOR COLUMN */}
                        <div>
                          <label className="block text-[11px] uppercase tracking-wider font-medium mb-1.5" style={{ color: C.textDim }}>Post content (edit freely)</label>
                          <textarea
                            value={publishModal.text}
                            onChange={e => setPublishModal(m => ({ ...m, text: e.target.value }))}
                            rows={16}
                            className="w-full rounded-lg px-3 py-2.5 text-sm outline-none resize-y"
                            style={{ background: C.bg, border: `1px solid ${overLimit ? C.error : C.border}`, color: C.text, fontFamily: "inherit" }}
                            data-testid="publish-modal-text"
                            placeholder="Write your LinkedIn post..."
                          />
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs" style={{ color: overLimit ? C.error : C.textDim }}>
                              {charCount}/3000 characters{overLimit && " — too long"}
                            </span>
                          </div>
                          <div className="mt-3 p-3 rounded-lg text-xs" style={{ background: `${C.secondary}10`, color: C.textMuted }}>
                            💡 The AI draft was extracted from the task output. Tweak freely — this is your last chance to edit before LinkedIn receives it.
                          </div>
                        </div>

                        {/* LINKEDIN PREVIEW COLUMN */}
                        <div>
                          <label className="block text-[11px] uppercase tracking-wider font-medium mb-1.5" style={{ color: C.textDim }}>
                            Live preview <span style={{ color: "#0A66C2" }}>• How it looks on LinkedIn</span>
                          </label>
                          <div className="rounded-xl p-4" style={{ background: "#F3F2EF", border: `1px solid ${C.border}`, minHeight: 400 }}>
                            {(() => {
                              const isCompany = publishModal.target && publishModal.target.startsWith("urn:li:organization:");
                              const companyName = isCompany
                                ? (linkedinOrgs.organizations.find(o => o.urn === publishModal.target)?.name || "Your Company")
                                : null;
                              return (
                                <LinkedInPreview
                                  text={publishModal.text}
                                  authorName={isCompany ? companyName : (linkedinStatus.linkedin_name || "Your name")}
                                  authorTitle={isCompany ? "Company Page" : (linkedinStatus.linkedin_email || "")}
                                  authorPicture={isCompany ? null : linkedinStatus.linkedin_picture}
                                  targetLabel={targetLabel}
                                  isCompany={isCompany}
                                />
                              );
                            })()}
                          </div>
                          <p className="text-[11px] mt-2 text-center" style={{ color: C.textDim }}>
                            Preview matches LinkedIn rendering. Hashtags, links, line breaks will appear as shown.
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 mt-5">
                        <button
                          onClick={() => setPublishModal(null)}
                          className="px-4 py-2 rounded-lg text-sm font-medium"
                          style={{ color: C.textDim, background: C.bg, border: `1px solid ${C.border}` }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={doPublish}
                          disabled={!publishModal.text.trim() || overLimit}
                          className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40"
                          style={{ background: "#0A66C2", color: "#fff" }}
                          data-testid="publish-modal-confirm"
                        >
                          <Send size={13} />Publish now
                        </button>
                      </div>
                    </>
                  )}

                  {/* PUBLISHING PHASE */}
                  {publishModal.phase === "publishing" && (
                    <div className="py-10 flex flex-col items-center gap-4">
                      <div className="w-12 h-12 rounded-full animate-spin" style={{ borderTop: `3px solid #0A66C2`, borderRight: `3px solid #0A66C2`, borderBottom: `3px solid ${C.border}`, borderLeft: `3px solid ${C.border}` }} />
                      <p className="text-sm" style={{ color: C.textDim }}>Sending to LinkedIn…</p>
                    </div>
                  )}

                  {/* SUCCESS PHASE */}
                  {publishModal.phase === "success" && (
                    <div className="py-6">
                      <div className="flex items-center gap-3 mb-4 p-4 rounded-xl" style={{ background: `${C.success}12`, border: `1px solid ${C.success}30` }}>
                        <CheckCircle2 size={20} style={{ color: C.success }} />
                        <div>
                          <p className="text-sm font-semibold" style={{ color: C.success }}>Post is live on LinkedIn</p>
                          <p className="text-xs" style={{ color: C.textDim }}>
                            Published {new Date().toLocaleString()} · as {targetLabel}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-end gap-2">
                        {publishModal.resultUrl && (
                          <a
                            href={publishModal.resultUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5"
                            style={{ background: "#0A66C2", color: "#fff" }}
                            data-testid="publish-success-view"
                          >
                            <ExternalLink size={13} />View on LinkedIn
                          </a>
                        )}
                        <button
                          onClick={() => setPublishModal(null)}
                          className="px-4 py-2 rounded-lg text-sm font-medium"
                          style={{ color: C.text, background: C.bg, border: `1px solid ${C.border}` }}
                          data-testid="publish-success-done"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}

                  {/* FAILED PHASE */}
                  {publishModal.phase === "failed" && (
                    <div className="py-6">
                      <div className="flex items-start gap-3 mb-4 p-4 rounded-xl" style={{ background: `${C.error}12`, border: `1px solid ${C.error}30` }}>
                        <XCircle size={20} style={{ color: C.error, flexShrink: 0, marginTop: 2 }} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold mb-1" style={{ color: C.error }}>Publish failed</p>
                          <p className="text-xs break-words" style={{ color: C.textDim }}>{publishModal.error}</p>
                        </div>
                      </div>
                      <p className="text-xs mb-4" style={{ color: C.textDim }}>
                        The post was NOT sent. You can edit the text and try again.
                      </p>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setPublishModal(null)}
                          className="px-4 py-2 rounded-lg text-sm font-medium"
                          style={{ color: C.textDim, background: C.bg, border: `1px solid ${C.border}` }}
                        >
                          Close
                        </button>
                        <button
                          onClick={() => setPublishModal(m => ({ ...m, phase: "preview" }))}
                          className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5"
                          style={{ background: C.primary, color: "#000" }}
                          data-testid="publish-retry"
                        >
                          <RefreshCw size={13} />Edit &amp; Retry
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </>
          );
        })()}
      </AnimatePresence>
    </div>
  );
};

export default AgentDashboard;
